import React, { useId, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button } from '../ui/button';
import { EmptyReport, money, number } from './ReportPanel';

const shortDate = date =>
  new Intl.DateTimeFormat('es-UY', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(
    new Date(`${date}T00:00:00Z`)
  );
const compact = value =>
  new Intl.NumberFormat('es-UY', { notation: 'compact', maximumFractionDigits: 1 }).format(value);

export default function RevenueChart({ daily = [], currency = 'UYU' }) {
  const [metric, setMetric] = useState('netRevenue');
  const gradient = `revenue-${useId().replace(/:/g, '')}`;
  const isRevenue = metric === 'netRevenue';
  const label = isRevenue ? 'Ingresos netos' : 'Ventas confirmadas';
  const format = value => (isRevenue ? money(value, currency) : number(value));
  return (
    <>
      <div className='mb-5 flex flex-wrap items-center justify-between gap-3'>
        <span className='text-xs text-muted-foreground'>
          {isRevenue ? `Importe (${currency})` : 'Cantidad de ventas'} · por día (UTC)
        </span>
        <div
          role='group'
          aria-label='Métrica del gráfico'
          className='flex gap-1 rounded-lg bg-muted p-1'
        >
          {[
            ['netRevenue', 'Ingresos'],
            ['confirmedSales', 'Ventas'],
          ].map(([value, title]) => (
            <Button
              key={value}
              type='button'
              size='sm'
              variant={metric === value ? 'default' : 'ghost'}
              aria-pressed={metric === value}
              onClick={() => setMetric(value)}
            >
              {title}
            </Button>
          ))}
        </div>
      </div>
      {daily.length ? (
        <>
          <div
            className='h-[280px] min-w-0 sm:h-[320px]'
            role='img'
            aria-label={`${label} por día en UTC`}
          >
            <ResponsiveContainer width='100%' height='100%' minWidth={0}>
              <AreaChart
                data={daily}
                margin={{ top: 12, right: 8, bottom: 8, left: 0 }}
                accessibilityLayer
              >
                <defs>
                  <linearGradient id={gradient} x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='0%' stopColor='hsl(var(--primary))' stopOpacity={0.25} />
                    <stop offset='100%' stopColor='hsl(var(--primary))' stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
                <XAxis
                  dataKey='date'
                  tickFormatter={shortDate}
                  minTickGap={36}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                />
                <YAxis
                  width={58}
                  tickFormatter={compact}
                  allowDecimals={isRevenue}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                />
                <ReferenceLine y={0} stroke='hsl(var(--border))' />
                <Tooltip
                  labelFormatter={shortDate}
                  formatter={value => [format(value), label]}
                  contentStyle={{
                    background: 'hsl(var(--popover))',
                    color: 'hsl(var(--popover-foreground))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 10,
                  }}
                  itemStyle={{ color: 'hsl(var(--popover-foreground))' }}
                />
                <Area
                  type='linear'
                  dataKey={metric}
                  name={label}
                  stroke='hsl(var(--primary))'
                  strokeWidth={2.5}
                  fill={`url(#${gradient})`}
                  baseValue={0}
                  isAnimationActive={false}
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          {!daily.some(row => row.confirmedSales || row.refundAmount) && (
            <p className='text-center text-sm text-muted-foreground'>
              Sin ventas ni reembolsos en este período.
            </p>
          )}
          <details className='mt-3 text-xs text-muted-foreground'>
            <summary className='cursor-pointer rounded py-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring'>
              Ver datos diarios
            </summary>
            <div className='mt-2 max-h-56 overflow-auto'>
              <table className='w-full text-sm'>
                <caption className='sr-only'>{label} por día (UTC)</caption>
                <thead>
                  <tr>
                    <th className='py-2 text-left'>Fecha (UTC)</th>
                    <th className='text-right'>
                      {label}
                      {isRevenue ? ` (${currency})` : ''}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {daily.map(row => (
                    <tr key={row.date} className='border-t border-border'>
                      <td className='py-2'>{row.date}</td>
                      <td className='text-right tabular-nums'>{format(row[metric])}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      ) : (
        <EmptyReport>No hay datos diarios disponibles.</EmptyReport>
      )}
    </>
  );
}
